import {useEffect, useMemo, useRef} from 'react';

import {IconOnlyButton} from 'shells/button/icon-only-button/icon-only-button';
import {ArrowDownUpRegularIcon} from 'shells/icon/svg/arrow-down-up-regular-icon';
import {SearchRegularIcon} from 'shells/icon/svg/search-regular-icon';
import {PageTabs} from 'shells/tabs/page-tabs/page-tabs';

import {getTabProps} from './helpers';
import type {RelatedPeepsTab} from './types';

interface Props {
    onSearchClicked: () => void;
    onFilterClicked: () => void;
    selectedTab: RelatedPeepsTab;
    onTabChange: (newTab: RelatedPeepsTab) => void;
    formerCount: number;
    currentCount: number;
    isSearching: boolean;
    hasSort: boolean;
    getSortByButtonRef?: (ref: HTMLButtonElement | null | undefined) => void;
}

export function TabSearchFilterSection(props: Props) {
    const {onTabChange} = props;

    const tabProps = useMemo(
        () => getTabProps(props.selectedTab, onTabChange, props.currentCount, props.formerCount),
        [props.selectedTab, onTabChange, props.currentCount, props.formerCount]
    );

    const prevFormerCountRef = useRef<number>(props.formerCount);

    // If the former tab was selected then hidden due to count changes, set to current tab
    useEffect(() => {
        const prevFormerCount = prevFormerCountRef.current;

        if (
            props.selectedTab === 'former' &&
            prevFormerCount > 0 &&
            props.formerCount === 0 &&
            !props.hasSort &&
            !props.isSearching
        ) {
            onTabChange('current');
        }

        prevFormerCountRef.current = props.formerCount;
    }, [props.selectedTab, props.formerCount, onTabChange, props.hasSort, props.isSearching]);

    if (tabProps.length === 0 && !props.isSearching && !props.hasSort) {
        return null;
    }

    return (
        <div className='px-12 flex justify-sb flex-dir-row pt-8 border-bottom'>
            <PageTabs tabs={tabProps} lessPadding={true} />
            <div className='flex flex-dir-row gap-8'>
                <IconOnlyButton
                    onClick={props.onSearchClicked}
                    icon={SearchRegularIcon}
                    isActive={props.isSearching}
                />
                <IconOnlyButton
                    onClick={props.onFilterClicked}
                    icon={ArrowDownUpRegularIcon}
                    getButtonRef={props.getSortByButtonRef}
                    isActive={props.hasSort}
                />
            </div>
        </div>
    );
}
